import { ChakraProvider, type ColorModeProviderProps } from '@chakra-ui/react';
import '@fontsource/source-sans-pro/300.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';

import { createRef, useLayoutEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import {
  initAuth,
  selectImpersonatedUser,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useAuthStore,
  useImpersonationSelector,
} from '@revelio/auth';
import {
  FeatureFlag,
  LoadingGifProvider,
  ServiceWorker,
  userTracking,
} from '@revelio/core';
import {
  newThemePlotColors,
  resetDarkBreakdownColors as resetDarkReplotsBreakdownColors,
  resetDarkPlotColors as resetDarkReplotsColors,
  resetDarkOtherColor as resetDarkReplotsOtherColor,
  resetBreakdownColors as resetReplotsBreakdownColors,
  resetPlotColors as resetReplotsColors,
  resetOtherColor as resetReplotsOtherColor,
  setDarkBreakdownColors as setDarkReplotsBreakdownColors,
  setDarkPlotColors as setDarkReplotsColors,
  setDarkOtherColor as setDarkReplotsOtherColor,
  setBreakdownColors as setReplotsBreakdownColors,
  setPlotColors as setReplotsColors,
  setOtherColor as setReplotsOtherColor,
} from '@revelio/replots';
import { ToastProvider, UITheme } from '@revelio/ui';

import './brands';

import { environment } from './environments/environment';
import { OpenReplayProvider } from './hooks/open-replay';
import { Next } from './next/next';
import {
  BrandProvider,
  useBrand,
  useBrandCssVars,
} from './shared/branding/brand-provider';
import { createBrandedTheme } from './shared/branding/create-branded-theme';
import { ColorModePreferenceSync } from './shared/color-mode/color-mode-preference-sync';
import { MaintenanceToast } from './shared/components';
import { TanstackQueryProvider } from './shared/data-fetch/tanstack-query-provider';
import { RouteAwareUrqlProvider } from './shared/data-fetch/urql-provider';
import './shared/firebase/firebase-performance';

import { KibanaLoggerProvider } from './shared/hooks/kibana-logger/kibana-logger';
import { kibanaLogger } from './shared/logging/kibana-logger';
import { UnleashFlags } from './shared/unleash-flags/unleash-flags';
import { useUnleashFlag } from './shared/unleash-flags/use-unleash-flag';
import { useUserPreferences } from './shared/user-preferences/hooks/useUserPreferences';
import {
  PreferenceScope,
  UserPreferenceKey,
} from './shared/user-preferences/types';

const isWilsonHost =
  typeof window !== 'undefined' &&
  window.location.hostname.toLowerCase().startsWith('wilson.');

initAuth({
  oryKratosRoot: environment.ORY_KRATOS_URL,
  dashMetaRoot: environment.DASH_META_API,
  accountPortalRoot: isWilsonHost
    ? environment.WILSON_ACCOUNT_PORTAL_ROOT
    : environment.ACCOUNT_PORTAL_ROOT,
  accountPortalBackendRoot: environment.ACCOUNT_PORTAL_BACKEND_ROOT,
});

if (document.cookie.includes('mswMockDashboard')) {
  const { worker } = require('./mocks/browser');
  worker.start({ onUnhandledRequest: 'bypass' });
}

/** Listen for uncaught promise rejections.
 * This is being setup to specifically handle uncaught rejections from an OpenReplay custom fetch function
 * We are catching the specific error and stopping its propagation */
window.addEventListener('unhandledrejection', (event) => {
  event.promise.catch((err) => {
    // ignore user aborted request thrown by OpenReplay
    if (
      err?.name === 'AbortError' &&
      err?.message === 'The user aborted a request.'
    ) {
      event.preventDefault();
      return;
    }
  });
});

userTracking.init(environment.mixpanelToken, {
  debug: false,
  opt_out_tracking_by_default: !environment.production,
});

const toastRef = createRef<HTMLDivElement>();
const toastOptions = {
  portalProps: {
    containerRef: toastRef,
  },
};

const uiThemePortalId = 'ui-portal-root';

function BrandedApp({ children }: { children: React.ReactNode }) {
  const realClientName = useAuthStore((s) => s.user?.client_name);
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);

  const clientName = useMemo(() => {
    // Wilson hostname always renders Wilson branding, regardless of who's
    // logged in. Lets pre-auth visitors and any cross-brand users on
    // wilson.* see the white-label experience.
    if (isWilsonHost) {
      return 'Wilson HR';
    }
    if (isImpersonating && impersonatedUser?.client_name !== undefined) {
      return impersonatedUser.client_name;
    }
    if (overrides?.clientName !== undefined) {
      return overrides.clientName;
    }
    return realClientName;
  }, [realClientName, isImpersonating, impersonatedUser, overrides]);

  return (
    <BrandProvider clientName={clientName}>
      <BrandedChakraProvider>{children}</BrandedChakraProvider>
    </BrandProvider>
  );
}

// Chakra's color mode is no longer used for theming — `data-theme` is the sole
// mechanism, driven by ColorModePreferenceSync — so keep Chakra permanently in
// light mode and never touch localStorage.
type ColorModeManager = NonNullable<ColorModeProviderProps['colorModeManager']>;
const lightModeManager: ColorModeManager = {
  type: 'localStorage',
  ssr: false,
  get: () => 'light',
  set: () => {
    // intentionally a no-op: color mode is never persisted
  },
};

function BrandedChakraProvider({ children }: { children: React.ReactNode }) {
  const brand = useBrand();
  useBrandCssVars();
  const brandedTheme = useMemo(() => createBrandedTheme(brand), [brand]);
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);
  const newThemeActive =
    isNewDashboardLayout &&
    (preferences[UserPreferenceKey.ColorMode] ?? 'legacy') !== 'legacy';

  useLayoutEffect(() => {
    if (brand.chartColors) {
      setReplotsColors(brand.chartColors);
    } else if (newThemeActive) {
      setReplotsColors(newThemePlotColors);
    } else {
      resetReplotsColors();
    }

    const darkChart = brand.darkChartColors ?? brand.chartColors;
    if (darkChart) {
      setDarkReplotsColors(darkChart);
    } else {
      resetDarkReplotsColors();
    }
  }, [brand.chartColors, brand.darkChartColors, newThemeActive]);

  useLayoutEffect(() => {
    // Breakdowns inherit the brand plot palette when no override is set. Note
    // there is deliberately no `newThemeActive` branch like the chart-colors
    // effect above: `plotColors` defaults to the legacy palette (so it must
    // switch to `newThemePlotColors` under the new layout), whereas the
    // breakdown palette already defaults to the new-theme `newBreakdownColors`.
    // An unbranded new-layout user therefore gets new-theme breakdown colours
    // via the reset path, so an extra branch here would be a no-op.
    const lightBreakdown = brand.breakdownColors ?? brand.chartColors;
    if (lightBreakdown) {
      setReplotsBreakdownColors(lightBreakdown);
    } else {
      resetReplotsBreakdownColors();
    }

    const darkBreakdown =
      brand.darkBreakdownColors ??
      brand.breakdownColors ??
      brand.darkChartColors ??
      brand.chartColors;
    if (darkBreakdown) {
      setDarkReplotsBreakdownColors(darkBreakdown);
    } else {
      resetDarkReplotsBreakdownColors();
    }
  }, [
    brand.breakdownColors,
    brand.darkBreakdownColors,
    brand.chartColors,
    brand.darkChartColors,
  ]);

  useLayoutEffect(() => {
    if (brand.otherColor) {
      setReplotsOtherColor(brand.otherColor);
    } else {
      resetReplotsOtherColor();
    }

    const darkOther = brand.darkOtherColor ?? brand.otherColor;
    if (darkOther) {
      setDarkReplotsOtherColor(darkOther);
    } else {
      resetDarkReplotsOtherColor();
    }
  }, [brand.otherColor, brand.darkOtherColor]);

  return (
    <ChakraProvider
      theme={brandedTheme}
      colorModeManager={lightModeManager}
      portalZIndex={40}
      toastOptions={toastOptions}
    >
      <ColorModePreferenceSync enabled={isNewDashboardLayout} />
      <LoadingGifProvider gif={brand.loadingGif}>{children}</LoadingGifProvider>
    </ChakraProvider>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <RouteAwareUrqlProvider>
        <OpenReplayProvider>
          <ServiceWorker />
          <TanstackQueryProvider>
            <UnleashFlags>
              <UITheme portalId={uiThemePortalId}>
                <BrandedApp>
                  <ToastProvider>
                    <Next />
                    <div ref={toastRef} />
                    <MaintenanceToast />
                  </ToastProvider>
                </BrandedApp>
                <div id={uiThemePortalId} />
              </UITheme>
            </UnleashFlags>
          </TanstackQueryProvider>
        </OpenReplayProvider>
      </RouteAwareUrqlProvider>
    ),
  },
]);

const rootContainer = document.getElementById('next') as Element;
const root = createRoot(rootContainer);

root.render(
  <KibanaLoggerProvider kibanaLogger={kibanaLogger}>
    <RouterProvider router={router} />
  </KibanaLoggerProvider>
);
